import { FormEvent, useState } from "react"
import { Head, Link, router } from "@inertiajs/react"
import { Search } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { AccountLifecycleBadge } from "@/components/AccountLifecycleBadge"
import { PageHeader } from "@/components/PageHeader"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"

interface AccountRow {
  id: number
  name: string
  owner_email: string | null
  lifecycle_status: string
  seat_count: number
  created_at: string
}

export default function AdminAccountsIndex({ accounts, q }: { accounts: AccountRow[]; q: string }) {
  const [query, setQuery] = useState(q)

  const search = (e: FormEvent) => {
    e.preventDefault()
    router.get("/admin/accounts", query ? { q: query } : {}, { preserveState: true })
  }

  return (
    <>
      <Head title="Accounts">
        <meta name="description" content="All Flywrite customer accounts — owners, status, and seats." />
        <meta property="og:title" content="Accounts" />
        <meta property="og:description" content="All Flywrite customer accounts — owners, status, and seats." />
      </Head>
      <AdminShell>
        <PageHeader
          title="Accounts"
          description={`${accounts.length} ${accounts.length === 1 ? "account" : "accounts"}${q ? ` matching “${q}”` : ""}.`}
        />

        <form onSubmit={search} className="mt-6 flex gap-2">
          <Input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by account name or member email…"
            aria-label="Search accounts"
            className="max-w-sm"
          />
          <Button type="submit" variant="secondary">
            <Search className="h-4 w-4" />
            Search
          </Button>
        </form>

        {accounts.length === 0 ? (
          <p className="mt-8 text-sm text-ink-muted">No accounts found.</p>
        ) : (
          <div className="mt-6 overflow-x-auto rounded-lg border border-hairline">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-hairline bg-surface text-left">
                  <th className="px-4 py-3 font-medium text-ink-muted">Account</th>
                  <th className="px-4 py-3 font-medium text-ink-muted">Owner</th>
                  <th className="px-4 py-3 font-medium text-ink-muted">Status</th>
                  <th className="px-4 py-3 text-right font-medium text-ink-muted">Seats</th>
                  <th className="px-4 py-3 font-medium text-ink-muted">Created</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((account) => (
                  <tr key={account.id} className="border-b border-hairline last:border-0">
                    <td className="px-4 py-3">
                      <Link href={`/admin/accounts/${account.id}`}>{account.name}</Link>
                    </td>
                    <td className="px-4 py-3">{account.owner_email ?? "—"}</td>
                    <td className="px-4 py-3">
                      <AccountLifecycleBadge status={account.lifecycle_status} />
                    </td>
                    <td className="px-4 py-3 text-right">{account.seat_count}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{formatDate(account.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AdminShell>
    </>
  )
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}
